import { type ActionCreatorWithPayload, type AsyncThunk } from '@reduxjs/toolkit'
import { type PaginatedResponse, StoreStatus } from 'common/utils/types'
import { type GetVendorListPayload } from 'features/vendors/vendorList.service'
import { type ListVendor } from 'features/vendors/vendorList.types'
import { debounce } from 'lodash'
import React, { type ChangeEvent, type ReactElement, useCallback, useEffect } from 'react'
import {
  InputAdornment,
  Skeleton,
  TablePagination,
  TextField,
  useTheme,
  IconButton as MuiIconButton,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import tendersBackground from 'assets/tendersBackground.svg'
import { BUTTON_SIZE, BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import IconButton from 'common/ui/icon-button'
import { ReactComponent as RightArrowIcon } from 'assets/rightArrowIcon.svg'
import { ReactComponent as ClearIcon } from 'assets/clearIcon.svg'
import { ReactComponent as SearchIcon } from 'assets/search.svg'
import { statusColors, statusMessage } from 'features/vendor/vendor-profile-layout/VendorProfileLayout'
import { getVendorStatus } from 'features/vendor/vendor.functions'
import { type Vendor } from 'features/vendor/vendor.types'
import { VENDORS_PER_PAGE } from 'features/vendors/vendorListFunctions'
import { Role } from 'common/utils/types'

import TooltipCellWrapper from '../components/tooltipCellWrapper/TooltipCellWrapper'
import CellWrapper from '../components/cellWrapper/CellWrapper'

import styles from './VendorList.module.scss'

interface VendorListProps {
  listName: 'verifiedVendorList' | 'rejectedVendorList' | 'pendingVendorList'
  countName: 'verifiedCount' | 'rejectedCount' | 'pendingCount'
  statusName: 'getVerifiedVendorListStatus' | 'getRejectedVendorListStatus' | 'getPendingVendorListStatus'
  searchName: 'verifiedSearch' | 'rejectedSearch' | 'pendingSearch'
  pageName: 'verifiedPage' | 'rejectedPage' | 'pendingPage'
  loadPageAction: AsyncThunk<
    { page: number; list: PaginatedResponse<ListVendor[]> },
    GetVendorListPayload,
    any
  >
  setSearchAction: ActionCreatorWithPayload<string>
  actionButtonText: string
  actionButtonHandler: (vendor: ListVendor) => void
}

const VendorList: React.FC<VendorListProps> = ({
  listName,
  countName,
  statusName,
  searchName,
  pageName,
  loadPageAction,
  setSearchAction,
  actionButtonText,
  actionButtonHandler,
}): ReactElement => {
  const { palette } = useTheme()

  const dispatch = useAppDispatch()

  const {
    [listName]: vendorList,
    [countName]: count,
    [statusName]: loadingStatus,
    [searchName]: searchString,
    [pageName]: currentPage,
  } = useAppSelector((state) => state.vendorList)

  useEffect(() => {
    loadVendors(0, searchString)
  }, [])

  const loadVendors = (page: number, search: string): void => {
    const payload = {
      ipp: VENDORS_PER_PAGE,
      page,
      search,
    }

    void dispatch(loadPageAction(payload))
  }

  const debouncedLoadVendors = useCallback(
    debounce((page: number, searchString: string) => {
      loadVendors(page, searchString)
    }, 500),
    [],
  )

  const setSearchString = (search: string): void => {
    dispatch(setSearchAction(search))
    debouncedLoadVendors(0, search)
  }

  const handlePageChange = (event: React.MouseEvent | null, page: number): void => {
    loadVendors(page, searchString)
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.backgroundImage}
        style={{
          backgroundImage: `url(${tendersBackground})`,
        }}
      />
      <div className={styles.searchContainer}>
        <TextField
          variant="outlined"
          placeholder="Pretraži"
          value={searchString}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setSearchString(e.target.value)
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '14px',
              color: palette.blue100.main,
              height: '70px',
            },
            '&.MuiTextField-root': {
              width: '100%',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <MuiIconButton
                  onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault()
                  }}
                  onClick={() => {
                    setSearchString(searchString ? '' : searchString)
                  }}
                >
                  {searchString ? <ClearIcon /> : <SearchIcon />}
                </MuiIconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
      <div id="vendorListWrapper" className={styles.vendorListWrapper}>
        {loadingStatus === StoreStatus.Succeeded && !vendorList?.length && (
          <div className={styles.noResults}>Nema rezulata za trenutnu pretragu</div>
        )}
        {loadingStatus !== StoreStatus.Loading && loadingStatus !== StoreStatus.Idle
          ? vendorList?.map((vendor) => (
              <div key={vendor.id} className={styles.vendorItem}>
                <TooltipCellWrapper
                  title="Naziv vendora"
                  value={
                    vendor?.user?.role === Role.consultant
                      ? vendor?.user
                        ? `${vendor.user.name?.first ?? ''} ${vendor.user.name?.last ?? ''}`
                        : '-'
                      : vendor?.name ?? '-'
                  }
                  width="16%"
                  style={{ minWidth: '130px', maxWidth: '130px' }}
                />
                <div className={styles.divider} />
                <CellWrapper title="PIB" value={vendor.tin} style={{ minWidth: '82px', maxWidth: '82px' }} />
                <div className={styles.divider} />
                <CellWrapper
                  title="E-mail"
                  value={vendor.user?.email}
                  width="32%"
                  style={{ minWidth: '300px', maxWidth: '300px' }}
                />
                <div className={styles.divider} />
                <CellWrapper
                  title="Status"
                  value={statusMessage(getVendorStatus(vendor as Vendor), { header: true })}
                  valueSx={{ color: statusColors(getVendorStatus(vendor as Vendor), palette) }}
                  style={{ minWidth: '90px', maxWidth: '90px' }}
                />
                <div className={styles.divider} />
                <div className={styles.buttons} style={{ maxWidth: '15%' }}>
                  {actionButtonText}
                  <IconButton
                    variant={BUTTON_VARIANTS.outlined}
                    size={BUTTON_SIZE.small}
                    sx={{
                      borderColor: palette.blue600.main,
                      backgroundColor: palette.blue600.main,
                      width: '24px',
                      minWidth: '24px',
                      height: '24px',
                      padding: 0,
                    }}
                    onClick={() => {
                      actionButtonHandler(vendor)
                    }}
                    icon={<RightArrowIcon style={{ stroke: 'white' }} />}
                  />
                </div>
              </div>
            ))
          : Array(VENDORS_PER_PAGE)
              .fill(0)
              .map((value, index) => (
                <Skeleton
                  key={`skeleton-${index}`}
                  variant="rounded"
                  width={'100%'}
                  height={96}
                  sx={{ backgroundColor: palette.blue700.main, marginRight: '1rem' }}
                />
              ))}
        <div className={styles.paginationWrapper}>
          {vendorList?.length > 0 && (
            <TablePagination
              count={count}
              onPageChange={handlePageChange}
              page={currentPage}
              rowsPerPage={VENDORS_PER_PAGE}
              rowsPerPageOptions={[VENDORS_PER_PAGE]}
              component="div"
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default VendorList
